import {
  LAUNCHDARKLY_LOCK_REASON,
  lockedByLaunchDarkly,
  UNLOCKED,
  type LaunchDarklyLock,
} from "~/features/feature-flags/shared/changeRules"
import {
  compositeKey,
  LAUNCHDARKLY_SYNC_AUTHOR,
  type CustomFlagCompanies,
  type DirectoryUser,
  type FlagOverrideRow,
  type OverrideScope,
} from "~/features/feature-flags/shared/contracts"
import { FEATURE_FLAGS, type FeatureFlagKey } from "~/features/feature-flags/shared/registry"
import type { FlagSnapshot } from "~/features/feature-flags/shared/snapshot"

export type DirectoryCompany = { id: string; name: string; slug: string }

/** The companies and users the panel names, and whether they are still loading. The seam of the
 * panel: the tRPC queries fill it in the app, tests fill it in memory. */
export type Directory = {
  companies: readonly DirectoryCompany[]
  users: readonly DirectoryUser[]
  loadingCompanies: boolean
  loadingUsers: boolean
}

const NO_OVERRIDES: FlagOverrideRow[] = []
const NO_COMPANIES: readonly string[] = []

const scopeKey = (flagKey: string, scope: OverrideScope) => compositeKey(flagKey, scope)

/** Names for the ids the panel shows. An id the directory doesn't know is named by the id. */
function flagNames(directory: Directory) {
  const companyById = new Map(directory.companies.map((company) => [company.id, company]))
  const userById = new Map(directory.users.map((user) => [user.id, user]))
  return {
    companyName: (orgId: string) => companyById.get(orgId)?.name ?? orgId,
    userName: (userId: string) =>
      userId === LAUNCHDARKLY_SYNC_AUTHOR
        ? "LaunchDarkly sync"
        : (userById.get(userId)?.name ?? userId),
    user: (userId: string) => userById.get(userId),
    isStaffUser: (userId: string) => userById.get(userId)?.isStaff ?? false,
    userOrgIds: (userId: string): readonly string[] => userById.get(userId)?.orgIds ?? [],
  }
}

/** What the flags resolve to and which changes the panel allows or warns about. */
function flagRules(
  snapshot: FlagSnapshot,
  overrides: readonly FlagOverrideRow[],
  customFlagCompanies: CustomFlagCompanies,
  lock: LaunchDarklyLock,
) {
  const byScope = Map.groupBy(overrides, (o) => scopeKey(o.flagKey, o.scope))
  const scoped = (flagKey: FeatureFlagKey, scope: OverrideScope): readonly FlagOverrideRow[] =>
    byScope.get(scopeKey(flagKey, scope)) ?? NO_OVERRIDES

  return {
    snapshot,
    lock,
    /** Why the panel can't change this flag's default (`default`) or company overrides (`org`). */
    lockReason: (flagKey: FeatureFlagKey, scope: OverrideScope) =>
      lockedByLaunchDarkly({ flagKey, scope }, lock) ? LAUNCHDARKLY_LOCK_REASON : null,
    /** One flag's company overrides, in stored order. */
    companyOverrides: (flagKey: FeatureFlagKey) => scoped(flagKey, "org"),
    /** The companies a custom flag is built for; none for other categories. */
    builtFor: (flagKey: FeatureFlagKey) => customFlagCompanies[flagKey] ?? NO_COMPANIES,
    /** Whether a custom flag is on for a company it wasn't built for. Allowed, but the panel warns. */
    isOffList: (flagKey: FeatureFlagKey, orgId: string) =>
      FEATURE_FLAGS[flagKey].category === "custom" &&
      !(customFlagCompanies[flagKey] ?? NO_COMPANIES).includes(orgId),
    /** One flag's user overrides, Hakio staff included, in stored order. */
    userOverrides: (flagKey: FeatureFlagKey) => scoped(flagKey, "user"),
  }
}

/** Everything the flag views read: one environment's flags, their overrides, the companies custom
 * flags are built for, who the companies and users are, and which flags LaunchDarkly still owns. */
export const createFlagPanel = (
  snapshot: FlagSnapshot,
  overrides: readonly FlagOverrideRow[],
  customFlagCompanies: CustomFlagCompanies,
  directory: Directory,
  lock: LaunchDarklyLock = UNLOCKED,
) => ({
  ...directory,
  ...flagNames(directory),
  ...flagRules(snapshot, overrides, customFlagCompanies, lock),
})

export type FlagPanel = ReturnType<typeof createFlagPanel>
export type FlagNames = ReturnType<typeof flagNames>
export type FlagRules = ReturnType<typeof flagRules>
